import {Injectable} from '@angular/core';
import {
  ContainerGoodData,
  DangerousGoodsDeclaration,
  GasData,
  HarbourDuesBerthVisit,
  HarbourDuesGoodsItem,
  HarbourDuesVisit,
  IE3ConsignmentProcessSummary,
  IE3GoodsItemSummary,
  IE3Place,
  Port,
  SolidData,
  StowageUnion,
  Visit,
} from "@portbase/bezoekschip-service-typescriptmodels";
import {concatAll, defaultIfEmpty, filter, Observable, reduce} from "rxjs";
import {map} from "rxjs/operators";
import {sendQuery} from "../../../common/utils";

@Injectable({
  providedIn: 'root'
})
export class HarbourDuesCargoService {

  getDischargedGoodsByTerminalCode(crn: string): Observable<Map<string, HarbourDuesGoodsItem[]>> {
    return sendQuery<IE3ConsignmentProcessSummary[]>("com.portbase.bezoekschip.common.api.consignments.queries.FindConsignments", {
      term: crn
    }, {showSpinner: true}).pipe(
      concatAll(),
      filter(value => this.isBulkConsignment(value)),
      map(summary => new Map([[this.getTerminalCode(summary), this.getDischargedGoods(summary)]])),
      reduce((a, b) => this.join(a, b)),
      defaultIfEmpty(new Map<string, HarbourDuesGoodsItem[]>())
    );
  }

  getLoadedGoodsByTerminalCode(visit: Visit) {
    return visit.dangerousGoodsDeclarations
      .flatMap(declaration => declaration.dangerousGoods.handlings)
      .filter(handling => handling.type === "loading")
      .map(loading => new Map([[this.getTerminalCodeOf(loading.berthVisitId, visit), this.getLoadedGoods(loading.shipToShip, loading.stowages, visit.dangerousGoodsDeclarations)]]))
      .reduce((a, b) => this.join(a, b), new Map<string, HarbourDuesGoodsItem[]>())
  }

  private getTerminalCodeOf(berthVisitId: string, visit: Visit) {
    return visit.visitDeclaration.portVisit.berthVisits.find(b => b.id === berthVisitId).berth.terminalCode;
  }

  private getLoadedGoods(shipToShip: boolean, stowages: StowageUnion[], declarations: DangerousGoodsDeclaration[]) {
    let goods = declarations
      .flatMap(declaration => declaration.dangerousGoods.goods)
      .filter(good => good.type === "containerGood" || good.type === "solid" || good.type === "gas");
    let loadedGoods: HarbourDuesGoodsItem[] = [];
    stowages.forEach(stowage => {
      let good = goods.find(good => good.id === stowage.goodId);
      if (good?.goodData?.unCode) {
        let multiplier = stowage.type === "container" || stowage.type === "breakBulk" ? 1 : 1000;
        loadedGoods.push(this.toHarbourDuesDangerousGoodsItem(shipToShip, good.goodData, stowage.portOfDischarge, stowage.weight, multiplier));
      }
    })
    return loadedGoods;
  }

  private toHarbourDuesDangerousGoodsItem(shipToShip: boolean, goodsData: SolidData | ContainerGoodData | GasData, portOfDischarge: Port, weight: number, multiplier: number): HarbourDuesGoodsItem {
    return {
      shipToShip: shipToShip,
      portOfHandling: portOfDischarge,
      weight: weight * multiplier,
      dangerInformation: {
        unCode: goodsData.unCode,
        name: goodsData.name
      }
    }
  }

  isBulkConsignment(summary: IE3ConsignmentProcessSummary): boolean {
    return !summary.cancelled &&
      summary.masterConsignment.dischargeTerminal &&
      this.getGoodsItemsFromAnyLevel(summary).every(value => !value.placements.length);
  }

  isVisitGoodsDeltaExceedingWeight(
    visit: HarbourDuesVisit,
    maxWeight?: number
  ): {
    isTotalExceeding: boolean;
    isBerthVisitExceedingList: boolean[];
    isAnyBerthVisitExceeding: boolean;
  } {
    const {
      totalWeight,
      berthVisitWeights
    } = this.calculateVisitNetGoodsWeight(visit);
    const isTotalExceeding = this.isExceedingWeight(
      totalWeight,
      maxWeight
    );
    const isBerthVisitExceedingList = berthVisitWeights.map(
      berthVisitWeight => this.isExceedingWeight(berthVisitWeight, maxWeight)
    );
    const isAnyBerthVisitExceeding = isBerthVisitExceedingList.some(
      berthVisitExceeding => berthVisitExceeding
    );
    return { isTotalExceeding, isBerthVisitExceedingList, isAnyBerthVisitExceeding };
  }

  isBerthVisitGoodsDeltaExceedingWeight(
    berthVisit: HarbourDuesBerthVisit,
    maxWeight?: number
  ): boolean {
    return this.isExceedingWeight(
      this.calculateBerthVisitNetGoodsWeight(berthVisit),
      maxWeight
    );
  }

  isGoodsExceedingWeight(
    goods?: HarbourDuesGoodsItem[],
    maxWeight?: number
  ): boolean {
    return this.isExceedingWeight(
      this.calculateGoodsTotalWeight(goods),
      maxWeight
    );
  }

  calculateVisitNetGoodsWeight(
    visit: HarbourDuesVisit
  ): { totalWeight: number; berthVisitWeights: number[]; } {
    const berthVisitWeights =  visit.berthVisits?.map(
      berthVisit => this.calculateBerthVisitNetGoodsWeight(berthVisit)
    ) || [];
    const totalWeight = berthVisitWeights.reduce(
      (a, b) => a + b, 0
    );
    return {totalWeight, berthVisitWeights};
  }

  calculateBerthVisitNetGoodsWeight(berthVisit: HarbourDuesBerthVisit): number {
    const loadedWeight = this.calculateGoodsTotalWeight(
      berthVisit.loadedGoods
    );
    const dischargedWeight = this.calculateGoodsTotalWeight(
      berthVisit.dischargedGoods
    );
    return loadedWeight - dischargedWeight;
  }

  calculateGoodsTotalWeight(goods?: HarbourDuesGoodsItem[]): number {
    return goods?.map(
      goodsItem => goodsItem.weight || 0
    ).reduce(
      (a, b) => a + b, 0
    ) || 0;
  }

  private isExceedingWeight(weight: number, maxWeight?: number): boolean {
    const absoluteWeight = Math.abs(weight);
    return maxWeight !== undefined
        && maxWeight !== null
        && absoluteWeight > maxWeight;
  }

  private getTerminalCode(summary: IE3ConsignmentProcessSummary) {
    return summary.masterConsignment.dischargeTerminal.terminalCode;
  }

  private getDischargedGoods(summary: IE3ConsignmentProcessSummary) {
    let placeOfLoading = summary.masterConsignment?.placeOfLoading;
    return this.getGoodsItemsFromAnyLevel(summary).map(goodsItem => this.toHarbourDuesGoodsItem(goodsItem, placeOfLoading));
  }

  private toHarbourDuesGoodsItem(summary: IE3GoodsItemSummary, placeOfLoading: IE3Place): HarbourDuesGoodsItem {
    return {
      goodsClassification:  summary?.commodity?.commodityCode?.harmonizedSystemSubHeadingCode ?  {
        code: summary?.commodity?.commodityCode?.harmonizedSystemSubHeadingCode,
        description: summary?.commodity?.descriptionOfGoods
      } : null,
      portOfHandling: placeOfLoading,
      weight: summary.grossWeight
    };
  }

  private join(a: Map<string, HarbourDuesGoodsItem[]>, b: Map<string, HarbourDuesGoodsItem[]>) {
    let result = new Map(a);
    for (const entry of b.entries()) {
      let key = entry[0];
      let value = entry[1];
      if (result.has(key)) {
        result.set(key, result.get(key).concat(value))
      } else {
        result.set(key, value);
      }
    }
    return result;
  }

  private getGoodsItemsFromAnyLevel(summary: IE3ConsignmentProcessSummary) {
    return summary.straight ? summary.masterConsignment.houseConsignments.flatMap(value => value.goodsItems) :
      summary.masterConsignment.goodsItems;
  }
}
